import {
  type MouseEvent,
  type PointerEvent,
  type TouchEvent,
  type UIEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { type UseSliderOptions, type UseSliderResult } from '../types'

const SCROLL_POSITION_EPSILON = 1
const SCROLL_END_DEBOUNCE_MS = 140
const SWIPE_THRESHOLD_PX = 32
const DRAG_THRESHOLD_PX = 6

const clampIndex = (value: number, slideCount: number) => {
  if (slideCount <= 0) {
    return 0
  }

  return Math.max(0, Math.min(value, slideCount - 1))
}

const getNearestSlideIndex = (track: HTMLDivElement) => {
  const slides = Array.from(track.children) as HTMLElement[]

  if (slides.length === 0) {
    return 0
  }

  const currentScrollLeft = track.scrollLeft

  return slides.reduce((closestIndex, slide, index, source) => {
    const currentDistance = Math.abs(slide.offsetLeft - currentScrollLeft)
    const bestDistance = Math.abs(
      source[closestIndex].offsetLeft - currentScrollLeft,
    )

    return currentDistance < bestDistance ? index : closestIndex
  }, 0)
}

export const useSlider = ({
  slideCount,
  strictSwipeStep = false,
  dragMode = 'step',
  activeIndex,
  defaultIndex = 0,
  onChange,
}: UseSliderOptions): UseSliderResult => {
  const trackRef = useRef<HTMLDivElement>(null!)
  const isControlled = activeIndex !== undefined
  const [innerIndex, setInnerIndex] = useState(() =>
    clampIndex(defaultIndex, slideCount),
  )
  const scrollEndTimeoutRef = useRef<number | null>(null)
  const isUserScrollingRef = useRef(false)
  const touchStartPointRef = useRef<{ x: number; y: number } | null>(null)
  const pointerDragRef = useRef<{
    pointerId: number | null
    startX: number
    startScrollLeft: number
    hasDragged: boolean
  }>({
    pointerId: null,
    startX: 0,
    startScrollLeft: 0,
    hasDragged: false,
  })
  const suppressClickAfterDragRef = useRef(false)
  const isInitialSyncRef = useRef(true)
  const lastNotifiedIndexRef = useRef<number | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const isStepSwipeMode = strictSwipeStep && dragMode === 'step'

  const currentIndex = useMemo(
    () => (isControlled ? clampIndex(activeIndex, slideCount) : innerIndex),
    [activeIndex, innerIndex, isControlled, slideCount],
  )

  const canGoPrev = currentIndex > 0
  const canGoNext = currentIndex < slideCount - 1

  const notifyChange = useCallback(
    (nextIndex: number) => {
      if (!onChange || lastNotifiedIndexRef.current === nextIndex) {
        return
      }

      lastNotifiedIndexRef.current = nextIndex
      onChange(nextIndex)
    },
    [onChange],
  )

  const goToIndex = useCallback(
    (
      index: number,
      behavior: ScrollBehavior = 'smooth',
      shouldNotify = true,
    ) => {
      const clampedIndex = clampIndex(index, slideCount)
      const track = trackRef.current

      if (!track) {
        return
      }

      const slides = Array.from(track.children) as HTMLElement[]
      const targetSlide = slides[clampedIndex]

      if (!targetSlide) {
        return
      }

      if (
        Math.abs(track.scrollLeft - targetSlide.offsetLeft) <=
        SCROLL_POSITION_EPSILON
      ) {
        if (!isControlled) {
          setInnerIndex((prev) => (prev === clampedIndex ? prev : clampedIndex))
        }

        if (shouldNotify) {
          notifyChange(clampedIndex)
        }

        return
      }

      track.scrollTo({
        left: targetSlide.offsetLeft,
        behavior,
      })

      if (!isControlled) {
        setInnerIndex((prev) => (prev === clampedIndex ? prev : clampedIndex))
      }

      if (shouldNotify) {
        notifyChange(clampedIndex)
      }
    },
    [isControlled, notifyChange, slideCount],
  )

  const goPrev = useCallback(() => {
    if (!canGoPrev) {
      return
    }

    goToIndex(currentIndex - 1)
  }, [canGoPrev, currentIndex, goToIndex])

  const goNext = useCallback(() => {
    if (!canGoNext) {
      return
    }

    goToIndex(currentIndex + 1)
  }, [canGoNext, currentIndex, goToIndex])

  const syncIndexByScroll = useCallback(() => {
    const track = trackRef.current

    if (!track || slideCount <= 0) {
      return
    }

    const nearestIndex = getNearestSlideIndex(track)

    if (nearestIndex === currentIndex) {
      return
    }

    if (!isControlled) {
      setInnerIndex(nearestIndex)
    }

    notifyChange(nearestIndex)
  }, [currentIndex, isControlled, notifyChange, slideCount])

  const onScroll = useCallback(
    (_event: UIEvent<HTMLDivElement>) => {
      isUserScrollingRef.current = true

      if (scrollEndTimeoutRef.current !== null) {
        window.clearTimeout(scrollEndTimeoutRef.current)
      }

      scrollEndTimeoutRef.current = window.setTimeout(() => {
        isUserScrollingRef.current = false
        scrollEndTimeoutRef.current = null
        syncIndexByScroll()
      }, SCROLL_END_DEBOUNCE_MS)
    },
    [syncIndexByScroll],
  )

  const onTouchStart = useCallback(
    (event: TouchEvent<HTMLDivElement>) => {
      if (!isStepSwipeMode) {
        return
      }

      const touch = event.changedTouches[0]

      if (!touch) {
        return
      }

      touchStartPointRef.current = {
        x: touch.clientX,
        y: touch.clientY,
      }
    },
    [isStepSwipeMode],
  )

  const onTouchEnd = useCallback(
    (event: TouchEvent<HTMLDivElement>) => {
      if (!isStepSwipeMode || !touchStartPointRef.current) {
        return
      }

      const touch = event.changedTouches[0]

      if (!touch) {
        touchStartPointRef.current = null
        return
      }

      const deltaX = touch.clientX - touchStartPointRef.current.x
      const deltaY = touch.clientY - touchStartPointRef.current.y
      const absX = Math.abs(deltaX)
      const absY = Math.abs(deltaY)

      touchStartPointRef.current = null

      if (absX < SWIPE_THRESHOLD_PX || absX <= absY) {
        return
      }

      if (deltaX < 0) {
        goNext()
        return
      }

      goPrev()
    },
    [goNext, goPrev, isStepSwipeMode],
  )

  const finishPointerDrag = useCallback((pointerId: number) => {
    if (pointerDragRef.current.pointerId !== pointerId) {
      return
    }

    const track = trackRef.current

    if (track?.hasPointerCapture(pointerId)) {
      track.releasePointerCapture(pointerId)
    }

    suppressClickAfterDragRef.current = pointerDragRef.current.hasDragged
    pointerDragRef.current.pointerId = null
    pointerDragRef.current.hasDragged = false
    setIsDragging(false)
  }, [])

  const onPointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (dragMode !== 'free') {
        return
      }

      if (event.pointerType === 'mouse' && event.button !== 0) {
        return
      }

      const track = trackRef.current

      if (!track) {
        return
      }

      pointerDragRef.current.pointerId = event.pointerId
      pointerDragRef.current.startX = event.clientX
      pointerDragRef.current.startScrollLeft = track.scrollLeft
      pointerDragRef.current.hasDragged = false
      suppressClickAfterDragRef.current = false
      setIsDragging(false)
      track.setPointerCapture(event.pointerId)
    },
    [dragMode],
  )

  const onPointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (
        dragMode !== 'free' ||
        pointerDragRef.current.pointerId !== event.pointerId
      ) {
        return
      }

      const track = trackRef.current

      if (!track) {
        return
      }

      const deltaX = event.clientX - pointerDragRef.current.startX

      if (
        !pointerDragRef.current.hasDragged &&
        Math.abs(deltaX) >= DRAG_THRESHOLD_PX
      ) {
        pointerDragRef.current.hasDragged = true
        setIsDragging(true)
      }

      if (pointerDragRef.current.hasDragged) {
        event.preventDefault()
      }

      track.scrollLeft = pointerDragRef.current.startScrollLeft - deltaX
    },
    [dragMode],
  )

  const onPointerUp = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      finishPointerDrag(event.pointerId)
    },
    [finishPointerDrag],
  )

  const onPointerCancel = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      finishPointerDrag(event.pointerId)
    },
    [finishPointerDrag],
  )

  const onClickCapture = useCallback((event: MouseEvent<HTMLDivElement>) => {
    if (!suppressClickAfterDragRef.current) {
      return
    }

    suppressClickAfterDragRef.current = false
    event.preventDefault()
    event.stopPropagation()
  }, [])

  useEffect(() => {
    return () => {
      if (scrollEndTimeoutRef.current !== null) {
        window.clearTimeout(scrollEndTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (slideCount <= 0) {
      return
    }

    const alignedIndex = clampIndex(currentIndex, slideCount)

    if (!isControlled && alignedIndex !== innerIndex) {
      setInnerIndex(alignedIndex)
    }

    if (
      !isInitialSyncRef.current &&
      isUserScrollingRef.current &&
      !isControlled
    ) {
      return
    }

    if (trackRef.current) {
      goToIndex(
        alignedIndex,
        isInitialSyncRef.current ? 'auto' : 'smooth',
        false,
      )
      isInitialSyncRef.current = false
    }
  }, [currentIndex, goToIndex, innerIndex, isControlled, slideCount])

  useEffect(() => {
    const handleResize = () => {
      if (slideCount <= 0) {
        return
      }

      goToIndex(currentIndex, 'auto', false)
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [currentIndex, goToIndex, slideCount])

  return {
    trackRef,
    currentIndex,
    canGoPrev,
    canGoNext,
    goPrev,
    goNext,
    goToIndex,
    isDragging,
    onScroll,
    onTouchStart,
    onTouchEnd,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onClickCapture,
  }
}
